import React, { useEffect, useState, useMemo } from "react";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import moment from "moment";

import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Pagination,
  Chip,
  CircularProgress,
  InputAdornment,
  Button,
  Fade
} from "@mui/material";

import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import { AssignmentTurnedIn, Circle } from "@mui/icons-material";

// Premium Dark Theme Palette
const BG_DARK = "#06362D";
const PANEL_DARK = "#06362D";
const DEEP_DARK = "#04241E"; 
const BORDER_DARK = "#D1A505";
const TEXT_PRIMARY = "#DEE2B1";
const TEXT_SECONDARY = "#D1A505";
const NEON_GREEN = "#FFD500"; 
const STRICT_DARK_BORDER = "rgba(0, 0, 0, 0.5)"; 

// --- CONTINUOUS ANIMATION CSS ---
const animationStyles = `
  /* Continuous Border Glowing / Pulsing (Dark to Gold) */
  @keyframes continuousBorderPulse {
    0% { border-color: ${STRICT_DARK_BORDER}; box-shadow: 0 8px 30px rgba(0,0,0,0.5); }
    50% { border-color: rgba(209, 165, 5, 0.4); box-shadow: 0 0 25px rgba(255, 213, 0, 0.1), inset 0 0 10px rgba(0,0,0,0.5); }
    100% { border-color: ${STRICT_DARK_BORDER}; box-shadow: 0 8px 30px rgba(0,0,0,0.5); }
  }

  /* Fast Blinking for Live Data Dot */
  @keyframes fastBlink {
    0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 8px ${NEON_GREEN}; }
    50% { opacity: 0.3; transform: scale(0.85); box-shadow: 0 0 0px transparent; }
  }

  /* Continuous Breathing (Zoom In/Out) for Headers */
  @keyframes continuousBreathe {
    0%, 100% { transform: scale(1); text-shadow: 0 4px 10px rgba(0,0,0,0.6); }
    50% { transform: scale(1.01); text-shadow: 0 6px 15px rgba(255, 213, 0, 0.2); }
  }

  /* Subtle Table Row Pulse */
  @keyframes tableRowPulse {
    0%, 100% { background-color: transparent; }
    50% { background-color: rgba(0, 0, 0, 0.15); }
  }

  /* Button Glowing Sweep */
  @keyframes buttonGlow {
    0% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0.4); }
    70% { box-shadow: 0 0 0 8px rgba(255, 213, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0); }
  }

  /* Icon Pulsing */
  @keyframes iconPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); color: ${TEXT_PRIMARY}; }
  }
  
  /* Initial load fade */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .anim-border-glow {
    animation: continuousBorderPulse 5s ease-in-out infinite;
  }
  .anim-live-dot {
    animation: fastBlink 1.5s infinite;
    border-radius: 50%;
  }
  .anim-breathe-text {
    animation: continuousBreathe 4s ease-in-out infinite;
  }
  .anim-icon-pulse {
    animation: iconPulse 3s ease-in-out infinite;
  }
  .anim-table-row {
    animation: tableRowPulse 4s infinite;
  }
  .anim-btn-glow {
    animation: buttonGlow 2s infinite;
  }
  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  
  /* Custom Scrollbar for Table */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.3); 
  }
  ::-webkit-scrollbar-thumb {
    background: ${BORDER_DARK}; 
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${NEON_GREEN}; 
  }
`;

function Checkouts() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    background: DEEP_DARK,
    color: TEXT_PRIMARY
  });

  const [bookings, setBookings] = useState([]);
  const [propertyId, setPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // State for what the user is currently typing
  const [searchInput, setSearchInput] = useState("");
  // State for what filter is actually applied after clicking the button
  const [appliedSearch, setAppliedSearch] = useState("");

  // Pagination
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);

  // ================= STEP 1: Get User Property ID =================
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const roleRef = doc(db, "authroles", user.uid);
          const snap = await getDoc(roleRef);
          if (snap.exists()) {
            setPropertyId(snap.data().propertyId);
          }
        } catch (error) {
          console.error("Error fetching role:", error);
        }
      }
    });
    return () => unsub();
  }, []);

  // ================= STEP 2: Fetch Base Data =================
  useEffect(() => {
    if (!propertyId) return;

    setLoading(true);
    try {
      // Fetching a larger pool (500) so client-side multi-field search has data to work with
      const q = query(
        collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("status", "==", "Checkout"),
        orderBy("extra4", "desc"),
        limit(500) 
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        setBookings(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            data: doc.data()
          }))
        );
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      Toast.fire({ icon: "error", title: error.message });
      setLoading(false);
    }
  }, [propertyId]);

  // ================= STEP 3: Handle Button Click =================
  const handleFetchData = () => {
    setAppliedSearch(searchInput);
    setPage(1); // Reset to page 1 whenever new search is applied
  };

  // Handle Enter Key press in the TextField
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleFetchData();
    }
  };

  // ================= STEP 4: Filter & Sort Data based on Applied Search =================
  const filteredBookings = useMemo(() => {
    const search = appliedSearch.toUpperCase().trim();

    const filtered = bookings.filter((item) => {
      // If no search is applied, show all fetched data
      if (!search) return true;

      const data = item.data;
      const custName = data.customers?.custname?.toUpperCase() || "";
      const mobile = data.customers?.mobile || "";
      const invoice = data.invoiceno?.toString().toUpperCase() || "";
      
      const rawCheckin = data.checkindate || "";
      const checkinDate1 = moment(rawCheckin).format("DD MMM YYYY").toUpperCase();
      const checkinDate2 = moment(rawCheckin).format("YYYY-MM-DD");
      const checkinDate3 = moment(rawCheckin).format("DD/MM/YYYY");

      const hasRoom = data.rooms?.some((r) => 
        r.roomno?.toString().toUpperCase().includes(search)
      );

      return (
        custName.includes(search) ||
        mobile.includes(search) ||
        invoice.includes(search) ||
        checkinDate1.includes(search) ||
        checkinDate2.includes(search) ||
        checkinDate3.includes(search) ||
        hasRoom
      );
    });

    // Sort: Newest checkout date first
    return filtered.sort((a, b) => {
      const dateA = new Date(a.data.checkoutdate || 0);
      const dateB = new Date(b.data.checkoutdate || 0);
      return dateB - dateA; 
    });
  }, [appliedSearch, bookings]);

  // Handle Pagination Slice
  const paginatedData = filteredBookings.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BG_DARK, overflowX: "hidden" }}>
      <style>{animationStyles}</style>

      <Box className="page-fade-in" p={2} sx={{ maxWidth: "1600px", margin: "0 auto" }}>
        
        {/* Top Header Bar */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h6" fontWeight={800} color={TEXT_PRIMARY} className="anim-breathe-text" sx={{ letterSpacing: '0.5px' }}>
            Checkout History
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ background: 'rgba(0, 0, 0, 0.4)', color: NEON_GREEN, padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', border: `1px solid ${STRICT_DARK_BORDER}` }}>
              <Circle sx={{ width: 10, height: 10, mr: 1, color: NEON_GREEN }} className="anim-live-dot" /> Archive System
            </Box>
            <Chip
              label={`Results: ${filteredBookings.length}`}
              size="medium"
              sx={{ 
                fontWeight: 800, 
                borderRadius: "8px", 
                backgroundColor: "rgba(255, 213, 0, 0.1)", 
                color: NEON_GREEN,
                border: `1px solid rgba(209, 165, 5, 0.3)`
              }}
            />
          </Box>
        </Box>

        {/* Main Content Card */}
        <Card
          className="anim-border-glow"
          sx={{
            borderRadius: "16px",
            backgroundColor: PANEL_DARK,
            border: `1px solid ${STRICT_DARK_BORDER}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            transition: 'all 0.3s'
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            
            {/* Search Input and Button */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={4} alignItems="center">
              <TextField
                fullWidth
                size="small"
                placeholder="Name, mobile, invoice, room, or date..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown} // Trigger search on Enter key
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: TEXT_SECONDARY, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: 450,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: DEEP_DARK,
                    color: TEXT_PRIMARY,
                    fontSize: "0.9rem",
                    border: `1px solid ${STRICT_DARK_BORDER}`,
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.3)",
                    transition: 'all 0.3s',
                    "&:hover": { borderColor: "rgba(0,0,0,0.8)", backgroundColor: "rgba(0,0,0,0.4)" },
                    "&.Mui-focused": { borderColor: BORDER_DARK, boxShadow: `0 0 10px rgba(209, 165, 5, 0.15)` }
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& input::placeholder": { color: TEXT_SECONDARY, opacity: 0.6 }
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleFetchData}
                className="anim-btn-glow"
                sx={{ 
                  textTransform: "none", 
                  fontWeight: 800,
                  height: "40px",
                  px: 4,
                  borderRadius: "10px",
                  backgroundColor: NEON_GREEN,
                  color: BG_DARK,
                  boxShadow: '0 4px 10px rgba(255, 213, 0, 0.3)',
                  transition: 'all 0.2s',
                  "&:hover": { 
                    backgroundColor: "#E6C000",
                    transform: "translateY(-1px)",
                    boxShadow: '0 6px 15px rgba(255, 213, 0, 0.4)'
                  },
                  "&:active": {
                    transform: "translateY(1px)"
                  }
                }}
              >
                Fetch Data
              </Button>
            </Stack>

            {/* Compact Table */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: DEEP_DARK, overflow: 'hidden', boxShadow: "inset 0 4px 10px rgba(0,0,0,0.2)" }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Sr No</TableCell>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Invoice</TableCell>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Customer Details</TableCell>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Stay Dates</TableCell>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Rooms</TableCell>
                    <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Total</TableCell>
                    <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Paid</TableCell>
                    <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Remaining</TableCell>
                    <TableCell align="center" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: "rgba(0,0,0,0.5)" }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody sx={{ backgroundColor: PANEL_DARK }}>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                        <CircularProgress size={32} sx={{ color: NEON_GREEN }} />
                        <Typography sx={{ mt: 2, color: TEXT_SECONDARY, fontSize: "0.9rem", fontWeight: 600, letterSpacing: '1px' }} className="anim-breathe-text">
                          SCANNING ARCHIVES...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <Fade in={true} timeout={300 + (index * 100)} key={item.id}>
                        <TableRow 
                            hover 
                            className="anim-table-row"
                            sx={{ 
                                "&:last-child td, &:last-child th": { border: 0 },
                                transition: 'all 0.2s',
                                "&:hover": { backgroundColor: "rgba(255, 213, 0, 0.05) !important", transform: 'scale(1.001)' } 
                            }}
                        >
                          <TableCell sx={{ fontSize: "0.85rem", color: TEXT_SECONDARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            {(page - 1) * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            <Typography variant="body2" fontWeight={700} fontSize="0.85rem" color={TEXT_PRIMARY} sx={{ fontFamily: 'monospace' }}>
                              #{item.data.invoiceno}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            <Typography variant="subtitle2" sx={{ textTransform: "capitalize", fontWeight: 700, fontSize: "0.85rem", color: TEXT_PRIMARY, letterSpacing: '0.3px' }}>
                              {item.data.customers?.custname || "N/A"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_SECONDARY, display: "block", fontSize: "0.75rem", mt: 0.5 }}>
                              Mob: {item.data.customers?.mobile || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            <Typography variant="caption" display="block" fontSize="0.75rem" sx={{ color: TEXT_SECONDARY, mb: 0.5 }}>
                              IN: <span style={{ color: TEXT_PRIMARY }}>{item.data.checkindate ? moment(item.data.checkindate).format("DD MMM YYYY") : "—"}</span>
                            </Typography>
                            <Typography variant="caption" display="block" fontWeight={600} fontSize="0.75rem" sx={{ color: TEXT_SECONDARY }}>
                              OUT: <span style={{ color: "#ef4444" }}>{item.data.checkoutdate ? moment(item.data.checkoutdate).format("DD MMM YYYY") : "—"}</span>
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {item.data.rooms?.map((r, i) => (
                                  <Chip
                                    key={i}
                                    label={r.roomno}
                                    size="small"
                                    sx={{ 
                                      height: "22px", 
                                      fontSize: "0.7rem", 
                                      backgroundColor: "rgba(0, 0, 0, 0.3)", 
                                      color: TEXT_PRIMARY, 
                                      fontWeight: 600, 
                                      border: `1px solid ${STRICT_DARK_BORDER}` 
                                    }}
                                  />
                                ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.85rem", fontWeight: 700, color: TEXT_PRIMARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            ₹{item.data.totalamount?.toLocaleString() ?? 0}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#4ade80", fontWeight: 700, fontSize: "0.85rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            ₹{item.data.advance?.toLocaleString() ?? 0}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: `1px solid ${STRICT_DARK_BORDER}`,
                              color: (item.data.remaining ?? 0) > 0 ? "#ef4444" : TEXT_SECONDARY,
                              fontWeight: 700,
                              fontSize: "0.85rem"
                            }}
                          >
                            ₹{item.data.remaining?.toLocaleString() ?? 0}
                          </TableCell>
                          <TableCell align="center" sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                            <IconButton
                              size="small"
                              href={`/print-invoice/${item.id}`}
                              sx={{ 
                                p: 1, 
                                backgroundColor: "rgba(255, 213, 0, 0.1)", 
                                color: NEON_GREEN, 
                                borderRadius: '8px',
                                border: `1px solid ${STRICT_DARK_BORDER}`,
                                transition: 'all 0.2s',
                                "&:hover": { backgroundColor: "rgba(209, 165, 5, 0.2)", transform: 'translateY(-2px)' } 
                              }}
                            >
                              <PrintIcon sx={{ fontSize: "1.2rem" }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                        <Box className="anim-icon-pulse" sx={{ opacity: 0.5, mb: 2 }}>
                            <AssignmentTurnedIn sx={{ fontSize: 40, color: TEXT_SECONDARY }} />
                        </Box>
                        <Typography sx={{ color: TEXT_SECONDARY, fontSize: "0.95rem", fontWeight: 500 }}>
                          No checkout records found matching your criteria.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {filteredBookings.length > rowsPerPage && (
              <Stack alignItems="flex-end" mt={4}>
                <Pagination
                  count={Math.ceil(filteredBookings.length / rowsPerPage)}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                  color="primary"
                  size="medium"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": { color: TEXT_PRIMARY, borderColor: STRICT_DARK_BORDER, backgroundColor: 'rgba(0,0,0,0.2)' },
                    "& .Mui-selected": { backgroundColor: `${NEON_GREEN} !important`, color: `${BG_DARK} !important`, fontWeight: 'bold' },
                    "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(255, 213, 0, 0.1)" }
                  }}
                />
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Checkouts;